import React, { useMemo } from "react";
import { Image, View, StyleSheet, TouchableOpacity } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import {
  MessageSquare,
  FileText,
  ClipboardCheck,
  Plus,
} from "lucide-react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import {
  QuestionLayout,
  IconInput,
} from "@src/packages/medical-history/components";
import { DocumentRow, MoonDateField } from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import { useDiseaseHistory } from "../../DiseaseHistoryContext";
import type { BreastTestingDetail } from "../../DiseaseHistoryContext";
import type { DiseaseHistoryStackParamList } from "../../DiseaseHistoryNavigator";
import { useFlowProgress } from "../../useFlowProgress";
import { BREAST_TEST_OPTIONS } from "../options";
import type { BreastTestType } from "../options";
import { useMedicalHistoryTheme } from "../../../../theme/MedicalHistoryTheme";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const testResultIllustration =
  require("@src/apps/moon/assets/images/vial-syringe.png") as number;

function getTestLabelKey(testType: string): string {
  return (
    BREAST_TEST_OPTIONS.find((o) => o.id === testType)?.labelKey ??
    "med_history.medical.disease_history.breast_testing.test_types.other"
  );
}

export function BreastTestingDetailsScreen() {
  const t = useTranslations();
  const capturePress = usePressCapture("BreastTestingDetailsScreen");
  const { state, updateState, save } = useDiseaseHistory();
  const navigation =
    useNavigation<NativeStackNavigationProp<DiseaseHistoryStackParamList>>();
  const progress = useFlowProgress("BreastTestingDetails");
  const theme = useMedicalHistoryTheme();

  // Group tests by testType, preserving order of first appearance
  const groupedTests = useMemo(() => {
    const groups: {
      testType: BreastTestType;
      labelKey: string;
      entries: { entry: BreastTestingDetail; globalIndex: number }[];
    }[] = [];
    const seenTypes = new Map<string, number>();

    state.breastTestings.forEach((entry, globalIndex) => {
      const existing = seenTypes.get(entry.testType);
      if (existing !== undefined) {
        groups[existing]!.entries.push({ entry, globalIndex });
      } else {
        seenTypes.set(entry.testType, groups.length);
        groups.push({
          testType: entry.testType,
          labelKey: getTestLabelKey(entry.testType),
          entries: [{ entry, globalIndex }],
        });
      }
    });

    return groups;
  }, [state.breastTestings]);

  const updateTest = (index: number, updates: Partial<BreastTestingDetail>) => {
    const updated = state.breastTestings.map((t, i) =>
      i === index ? { ...t, ...updates } : t,
    );
    updateState({ breastTestings: updated });
  };

  const removeResult = (globalIndex: number) => {
    const entry = state.breastTestings[globalIndex]!;
    const idsToDelete = entry.id
      ? [...state.breastTestingIdsToDelete, entry.id]
      : state.breastTestingIdsToDelete;
    updateState({
      breastTestings: state.breastTestings.filter((_, i) => i !== globalIndex),
      breastTestingIdsToDelete: idsToDelete,
    });
  };

  const addAnotherResult = (testType: BreastTestType) => {
    const newEntry: BreastTestingDetail = { testType };
    updateState({
      breastTestings: [...state.breastTestings, newEntry],
    });
  };

  const handleSave = async () => {
    await save();
    const currentIdx = state.navigationStack.indexOf("BreastTestingDetails");
    const nextRoute = state.navigationStack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t(
        "med_history.medical.disease_history.breast_testing.details_title",
      )}
      subtitle={t(
        "med_history.medical.disease_history.breast_testing.details_subtitle",
      )}
      onBack={() => navigation.goBack()}
      progress={progress}
      onSave={handleSave}
      saveDisabled={false}
      saveLabel={t("med_history.medical.shared.next")}
    >
      <View style={styles.container}>
        {groupedTests.map((group) => (
          <View key={group.testType} style={styles.group}>
            <View style={[GLASS_CARD_STYLE, styles.detailCard]}>
              <View style={styles.cardHeader}>
                <Text style={[styles.cardTitle, { color: theme.text }]}>
                  {group.testType === "other"
                    ? t(
                        "med_history.medical.disease_history.breast_testing.other_section_label",
                      )
                    : t(group.labelKey)}
                </Text>
              </View>

              {group.entries.map(({ entry, globalIndex }, entryIdx) => (
                <View key={globalIndex} style={styles.entryGroup}>
                  {entryIdx > 0 && (
                    <View
                      style={[
                        styles.resultDivider,
                        { backgroundColor: theme.inputBorder },
                      ]}
                    />
                  )}
                  {group.entries.length > 1 && (
                    <View style={styles.resultHeader}>
                      <Text
                        style={[
                          styles.resultLabel,
                          {
                            color: theme.textFaint,
                            fontSize: 12,
                            fontWeight: "500",
                          },
                        ]}
                      >
                        {t(
                          "med_history.medical.disease_history.breast_testing.result_label",
                          { params: { index: String(entryIdx + 1) } },
                        )}
                      </Text>
                      <TouchableOpacity
                        onPress={() => {
                          capturePress(
                            pressLabel(t("med_history.medical.shared.remove")),
                            "med_history.medical.shared.remove",
                          );
                          removeResult(globalIndex);
                        }}
                      >
                        <Text
                          style={{
                            color: "#f87171",
                            fontSize: 12,
                            fontWeight: "600",
                          }}
                        >
                          {t("med_history.medical.shared.remove")}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  )}

                  {entry.testType === "other" && (
                    <IconInput
                      icon={FileText}
                      label={t(
                        "med_history.medical.disease_history.breast_testing.other_name_label",
                      )}
                      placeholder={t(
                        "med_history.medical.disease_history.breast_testing.other_name_placeholder",
                      )}
                      value={entry.other ?? ""}
                      onChangeText={(text) =>
                        updateTest(globalIndex, { other: text || null })
                      }
                    />
                  )}

                  <MoonDateField
                    label={t(
                      "med_history.medical.disease_history.breast_testing.exam_date_label",
                    )}
                    placeholder={t(
                      "med_history.medical.disease_history.breast_testing.exam_date_placeholder",
                    )}
                    value={entry.testDate ?? ""}
                    onChange={(iso) =>
                      updateTest(globalIndex, { testDate: iso || null })
                    }
                  />

                  <IconInput
                    icon={ClipboardCheck}
                    label={t(
                      "med_history.medical.disease_history.breast_testing.outcome_label",
                    )}
                    placeholder={t(
                      "med_history.medical.disease_history.breast_testing.outcome_placeholder",
                    )}
                    value={entry.outcome ?? ""}
                    onChangeText={(text) =>
                      updateTest(globalIndex, { outcome: text || null })
                    }
                  />

                  <IconInput
                    icon={MessageSquare}
                    label={t(
                      "med_history.medical.disease_history.breast_testing.notes_label",
                    )}
                    placeholder={t(
                      "med_history.medical.disease_history.breast_testing.notes_placeholder",
                    )}
                    value={entry.notes ?? ""}
                    onChangeText={(text) =>
                      updateTest(globalIndex, { notes: text || null })
                    }
                    multiline
                    numberOfLines={3}
                  />
                </View>
              ))}
            </View>

            <DocumentRow
              onPress={() => addAnotherResult(group.testType)}
              accessibilityLabel={t(
                "med_history.medical.disease_history.breast_testing.add_another_result",
                { params: { label: t(group.labelKey).toLowerCase() } },
              )}
              leading={
                <Image
                  source={testResultIllustration}
                  resizeMode="contain"
                  style={styles.addIllustration}
                />
              }
              title={t(
                "med_history.medical.disease_history.breast_testing.add_another_result",
                { params: { label: t(group.labelKey).toLowerCase() } },
              )}
              trailing={
                <Plus
                  size={18}
                  color={EMBER.colors.textLight}
                  strokeWidth={1.5}
                />
              }
              showChevron={false}
              style={styles.addRow}
            />
          </View>
        ))}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
    gap: 16,
  },
  detailCard: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
  },
  group: {
    width: "100%",
    gap: 8,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: "600",
  },
  entryGroup: {
    gap: 16,
    marginBottom: 16,
  },
  resultDivider: {
    height: 1,
    marginBottom: 4,
  },
  resultHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  resultLabel: {
    letterSpacing: 0.5,
  },
  addRow: {
    width: "100%",
  },
  addIllustration: {
    width: 34,
    height: 34,
  },
});
